import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ShoppingCart, Plus, Search, Trash2, Send, Package, 
  ChevronRight, Check, AlertTriangle, Phone, MessageCircle,
  Clock, CheckCircle, XCircle, TrendingUp, TrendingDown,
  FileText, Camera, Upload, X, Edit, RefreshCw, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Helper per ottenere headers con autenticazione e tenant
// Usa l'helper centralizzato per consistenza
import { getApiHeaders } from '../utils/apiHelpers';
const getAuthHeaders = getApiHeaders;

const OrdiniPage = ({ setCurrentPage }) => {
  const [activeTab, setActiveTab] = useState('lista_spesa'); // lista_spesa, ordini_fornitore, ddt_attesa
  const [loading, setLoading] = useState(false);
  
  // Lista della spesa
  const [listaSpesa, setListaSpesa] = useState([]);
  const [searchProdotto, setSearchProdotto] = useState('');
  const [suggerimenti, setSuggerimenti] = useState([]);
  const [showSuggerimenti, setShowSuggerimenti] = useState(false);
  const [selectedProdotto, setSelectedProdotto] = useState(null);
  const [showQuantitaModal, setShowQuantitaModal] = useState(false);
  const [quantita, setQuantita] = useState('');
  const [unitaMisura, setUnitaMisura] = useState('kg');
  
  // Ordini fornitore
  const [ordiniFornitore, setOrdiniFornitore] = useState([]);
  
  // DDT in attesa
  const [ddtAttesa, setDdtAttesa] = useState([]);
  const [selectedDDT, setSelectedDDT] = useState(null);
  const [showAnalisiModal, setShowAnalisiModal] = useState(false);
  const [analisiResult, setAnalisiResult] = useState(null);
  
  // Nuovo flusso DDT con step (come GestioneBollePage)
  const [ddtStep, setDdtStep] = useState(1); // 1=upload, 2=verifica, 3=confronto
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [prodottiVerifica, setProdottiVerifica] = useState([]);
  const [confrontoOrdine, setConfrontoOrdine] = useState(null);
  const [analyzingDDT, setAnalyzingDDT] = useState(false);
  
  // Fornitori
  const [fornitori, setFornitori] = useState([]);
  const [showFornitoriModal, setShowFornitoriModal] = useState(false);
  const [editingFornitore, setEditingFornitore] = useState(null);
  const [searchFornitore, setSearchFornitore] = useState('');

  // Foto Lista Spesa
  const [showFotoAnalisiModal, setShowFotoAnalisiModal] = useState(false);
  const [fotoAnalisiResult, setFotoAnalisiResult] = useState(null);
  const [loadingFoto, setLoadingFoto] = useState(false);
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [showImportTestoModal, setShowImportTestoModal] = useState(false);
  const [testoImport, setTestoImport] = useState('');

  // Modifica item lista spesa
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editItemData, setEditItemData] = useState({
    nome: '',
    quantita: '',
    unita_misura: 'kg',
    prezzo_stimato: ''
  });

  // Carica dati iniziali
  useEffect(() => {
    loadListaSpesa();
    loadOrdiniFornitore();
    loadDDTAttesa();
    loadFornitori();
  }, []);

  // Carica lista spesa dal database
  const loadListaSpesa = async () => {
    try {
      const res = await axios.get(`${API}/gestione/ordini/lista-spesa`, {
        headers: getAuthHeaders()
      });
      setListaSpesa(res.data.items || []);
    } catch (error) {
      console.error('Errore caricamento lista spesa:', error);
    }
  };

  // Debounce per ricerca prodotti
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchProdotto.length >= 2) {
        searchProdotti();
      } else {
        setSuggerimenti([]);
        setShowSuggerimenti(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchProdotto]);

  const searchProdotti = async () => {
    try {
      const res = await axios.get(`${API}/gestione/ordini/suggerisci-prodotto?q=${encodeURIComponent(searchProdotto)}`, {
        headers: getAuthHeaders()
      });
      setSuggerimenti(res.data.suggerimenti || []);
      setShowSuggerimenti(true);
    } catch (error) {
      console.error('Errore ricerca prodotti:', error);
    }
  };

  const loadOrdiniFornitore = async () => {
    try {
      // Carica TUTTI gli ordini (non solo da_inviare)
      const res = await axios.get(`${API}/gestione/ordini-fornitore`, {
        headers: getAuthHeaders()
      });
      setOrdiniFornitore(res.data.ordini || []);
    } catch (error) {
      console.error('Errore caricamento ordini:', error);
    }
  };

  const loadDDTAttesa = async () => {
    try {
      const res = await axios.get(`${API}/gestione/ddt?solo_attesa=true`, {
        headers: getAuthHeaders()
      });
      // Filtra solo DDT in attesa
      const inAttesa = (res.data.ddt || []).filter(d => d.status === 'in_attesa');
      setDdtAttesa(inAttesa);
    } catch (error) {
      console.error('Errore caricamento DDT:', error);
    }
  };

  const loadFornitori = async () => {
    try {
      const res = await axios.get(`${API}/gestione/fornitori`, {
        headers: getAuthHeaders()
      });
      setFornitori(res.data.fornitori || []);
    } catch (error) {
      console.error('Errore caricamento fornitori:', error);
    }
  };

  // Apri modal quantità quando si seleziona un prodotto
  const handleSelectProdotto = (prodotto, fornitore) => {
    setSelectedProdotto({ prodotto, fornitore });
    setShowSuggerimenti(false);
    setSearchProdotto('');
    setQuantita('');
    setUnitaMisura(fornitore.unita_misura || 'kg');
    setShowQuantitaModal(true);
  };

  const aggiungiAllaLista = async () => {
    if (!selectedProdotto) return;
    
    if (!quantita || parseFloat(quantita) <= 0) {
      toast.error('Inserisci una quantità valida');
      return;
    }

    const { prodotto, fornitore } = selectedProdotto;
    
    try {
      await axios.post(`${API}/gestione/ordini/lista-spesa`, {
        nome: prodotto.nome,
        quantita: parseFloat(quantita),
        unita_misura: unitaMisura,
        fornitore_suggerito: fornitore.nome,
        prezzo_stimato: fornitore.ultimo_prezzo
      }, {
        headers: getAuthHeaders()
      });
      
      // Ricarica la lista dal database
      await loadListaSpesa();
      
      setQuantita('');
      setSelectedProdotto(null);
      setShowQuantitaModal(false);
      toast.success(`${prodotto.nome} aggiunto alla lista`);
    } catch (error) {
      console.error('Errore aggiunta prodotto:', error);
      toast.error('Errore durante l\'aggiunta del prodotto');
    }
  };

  const chiudiQuantitaModal = () => {
    setShowQuantitaModal(false);
    setSelectedProdotto(null);
    setQuantita('');
  };

  // Apri modal modifica item
  const apriModificaItem = (item) => {
    setEditingItem(item);
    setEditItemData({
      nome: item.nome,
      quantita: item.quantita.toString(),
      unita_misura: item.unita_misura,
      prezzo_stimato: item.prezzo_stimato.toString()
    });
    setShowEditItemModal(true);
  };

  // Salva modifiche item
  const salvaModificheItem = async () => {
    if (!editingItem) return;
    
    if (!editItemData.quantita || parseFloat(editItemData.quantita) <= 0) {
      toast.error('Inserisci una quantità valida');
      return;
    }

    try {
      await axios.put(`${API}/gestione/ordini/lista-spesa/${editingItem.id}`, {
        nome: editItemData.nome,
        quantita: parseFloat(editItemData.quantita),
        unita_misura: editItemData.unita_misura,
        prezzo_stimato: parseFloat(editItemData.prezzo_stimato) || 0
      }, {
        headers: getAuthHeaders()
      });
      
      await loadListaSpesa();
      setShowEditItemModal(false);
      setEditingItem(null);
      toast.success('Prodotto modificato');
    } catch (error) {
      console.error('Errore modifica prodotto:', error);
      toast.error('Errore durante la modifica');
    }
  };

  const rimuoviDallaLista = async (id) => {
    try {
      await axios.delete(`${API}/gestione/ordini/lista-spesa/${id}`, {
        headers: getAuthHeaders()
      });
      // Ricarica la lista
      await loadListaSpesa();
      toast.success('Prodotto rimosso');
    } catch (error) {
      console.error('Errore rimozione prodotto:', error);
      toast.error('Errore durante la rimozione');
    }
  };

  // Auto-assegna fornitori ai prodotti senza fornitore
  const autoAssegnaFornitori = async () => {
    const senzaFornitore = listaSpesa.filter(item => !item.fornitore_nome);
    if (senzaFornitore.length === 0) {
      toast.info('Tutti i prodotti hanno già un fornitore');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/gestione/ordini/lista-spesa/auto-assegna-fornitori`, {}, {
        headers: getAuthHeaders()
      });
      
      if (res.data.assegnati > 0) {
        toast.success(`${res.data.assegnati} fornitori assegnati automaticamente`);
        await loadListaSpesa();
      }
      
      if (res.data.non_trovati?.length > 0) {
        toast.warning(`${res.data.non_trovati.length} prodotti senza storico fatture`);
      }
    } catch (error) {
      console.error('Errore auto-assegnazione:', error);
      toast.error('Errore durante l\'auto-assegnazione');
    } finally {
      setLoading(false);
    }
  };

  const generaOrdiniPerFornitore = async () => {
    if (listaSpesa.length === 0) {
      toast.error('La lista della spesa è vuota');
      return;
    }
    
    // Controlla se ci sono prodotti senza fornitore
    const senzaFornitore = listaSpesa.filter(item => !item.fornitore_nome);
    if (senzaFornitore.length > 0) {
      // Prova prima ad auto-assegnare
      try {
        const autoRes = await axios.post(`${API}/gestione/ordini/lista-spesa/auto-assegna-fornitori`, {}, {
          headers: getAuthHeaders()
        });
        
        if (autoRes.data.assegnati > 0) {
          toast.info(`${autoRes.data.assegnati} fornitori assegnati automaticamente`);
          await loadListaSpesa();
        }
        
        // Ricontrolla dopo l'auto-assegnazione
        const listaAggiornata = await axios.get(`${API}/gestione/ordini/lista-spesa`, {
          headers: getAuthHeaders()
        });
        const ancorasSenzaFornitore = (listaAggiornata.data.items || []).filter(item => !item.fornitore_nome);
        
        if (ancorasSenzaFornitore.length > 0) {
          toast.warning(`${ancorasSenzaFornitore.length} prodotti senza fornitore verranno raggruppati come "Senza Fornitore"`);
        }
      } catch (error) {
        console.error('Errore auto-assegnazione:', error);
      }
    }

    setLoading(true);
    try {
      // Crea ordine principale
      const ordineRes = await axios.post(`${API}/gestione/ordini`, {
        nome: `Ordine ${new Date().toLocaleDateString('it-IT')}`,
        prodotti: listaSpesa.map(item => ({
          nome: item.nome,
          quantita: item.quantita,
          unita_misura: item.unita_misura,
          fornitore_id: item.fornitore_id,
          fornitore_nome: item.fornitore_nome,
          prezzo_stimato: item.prezzo_stimato
        }))
      }, {
        headers: getAuthHeaders()
      });

      // Genera ordini per fornitore
      const genRes = await axios.post(`${API}/gestione/ordini/${ordineRes.data.ordine.id}/genera-per-fornitore`, {}, {
        headers: getAuthHeaders()
      });

      // Svuota lista spesa nel database
      await axios.delete(`${API}/gestione/ordini/lista-spesa`, {
        headers: getAuthHeaders()
      });

      // Mostra messaggio appropriato
      const ordiniCreati = genRes.data.ordini_creati?.length || 0;
      const senzaFornitore = genRes.data.prodotti_senza_fornitore?.length || 0;
      
      if (ordiniCreati > 0) {
        toast.success(`${ordiniCreati} ordini creati per fornitore`);
      } else if (senzaFornitore > 0) {
        toast.warning(`${senzaFornitore} prodotti senza fornitore assegnato`);
      }
      
      setListaSpesa([]);
      setActiveTab('ordini_fornitore');
      loadOrdiniFornitore();
      loadDDTAttesa();
    } catch (error) {
      console.error('Errore generazione ordini:', error);
      toast.error('Errore nella generazione ordini');
    } finally {
      setLoading(false);
    }
  };

  const inviaWhatsApp = async (ordine) => {
    if (!ordine.whatsapp_link) {
      toast.error('Numero WhatsApp non configurato per questo fornitore');
      return;
    }
    
    window.open(ordine.whatsapp_link, '_blank');
    
    // Segna come inviato e crea DDT in attesa
    try {
      await axios.put(`${API}/gestione/ordini-fornitore/${ordine.id}/inviato`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Ordine inviato! DDT in attesa creato.');
      loadOrdiniFornitore();
      loadDDTAttesa(); // Ricarica DDT in attesa
    } catch (error) {
      console.error('Errore:', error);
    }
  };

  const eliminaOrdineFornitore = async (ordineId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo ordine?')) return;
    
    try {
      await axios.delete(`${API}/gestione/ordini-fornitore/${ordineId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Ordine eliminato');
      loadOrdiniFornitore();
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore eliminazione ordine');
    }
  };

  const eliminaDDT = async (ddtId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo DDT?')) return;
    
    try {
      await axios.delete(`${API}/gestione/ddt/${ddtId}`, {
        headers: getAuthHeaders()
      });
      toast.success('DDT eliminato');
      loadDDTAttesa();
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore eliminazione DDT');
    }
  };

  // Funzione per gestire upload file DDT e analisi
  const handleDDTFileUpload = async (file) => {
    if (!selectedDDT) {
      toast.error('Nessun DDT selezionato');
      return;
    }

    setLoading(true);
    toast.info('Analisi in corso...');

    try {
      // Converti file in base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Data = reader.result;
          
          // Determina il tipo di file
          const fileName = file.name.toLowerCase();
          let endpoint, payload;
          
          if (fileName.endsWith('.pdf')) {
            endpoint = `${API}/gestione/ddt/analizza-documento`;
            payload = { file_base64: base64Data, tipo_file: 'pdf' };
          } else {
            endpoint = `${API}/gestione/ddt/analizza-immagine`;
            payload = { immagine_base64: base64Data };
          }
          
          const res = await axios.post(endpoint, payload, {
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            timeout: 180000
          });
          
          if (res.data.success) {
            // Aggiorna il DDT con i dati estratti
            const analisi = res.data.analisi || res.data.dati;
            setAnalisiResult({
              success: true,
              dati_estratti: { prodotti: analisi.prodotti || [] },
              fornitore: analisi.fornitore,
              data_ddt: analisi.data_ddt,
              numero_ddt: analisi.numero_ddt
            });
            setShowAnalisiModal(true);
            toast.success(`Analisi completata: ${analisi.prodotti?.length || 0} prodotti trovati`);
          } else {
            toast.error(res.data.detail || 'Errore durante l\'analisi');
          }
        } catch (error) {
          console.error('Errore analisi:', error);
          toast.error(error.response?.data?.detail || 'Errore durante l\'analisi del documento');
        } finally {
          setLoading(false);
        }
      };
      
      reader.onerror = () => {
        toast.error('Errore nella lettura del file');
        setLoading(false);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Errore upload:', error);
      toast.error('Errore durante l\'upload');
      setLoading(false);
    }
  };

  // Funzione per selezionare file DDT (step 1)
  const handleDDTFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile({ file, name: file.name, tipoFile: file.type.includes('pdf') ? 'pdf' : 'image' });
      if (!file.type.includes('pdf')) {
        setPreviewUrl(URL.createObjectURL(file));
      }
    }
  };

  // Funzione per analizzare DDT selezionato (step 1 -> step 2)
  const analyzeDDTFile = async () => {
    if (!selectedFile || !selectedDDT) return;
    
    setAnalyzingDDT(true);
    // Rimosso toast.info per evitare sfarfallio
    
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Data = reader.result;
          const endpoint = selectedFile.tipoFile === 'pdf' 
            ? `${API}/gestione/ddt/analizza-documento`
            : `${API}/gestione/ddt/analizza-immagine`;
          
          const payload = selectedFile.tipoFile === 'pdf'
            ? { file_base64: base64Data, tipo_file: 'pdf' }
            : { immagine_base64: base64Data };
          
          const res = await axios.post(endpoint, payload, {
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            timeout: 180000
          });
          
          if (res.data.success) {
            const analisi = res.data.analisi || res.data.dati;
            
            // Prepara prodotti per verifica
            const prodottiConStato = (analisi.prodotti || []).map((p, idx) => ({
              ...p,
              id: idx,
              stato: 'ricevuto', // Default: ricevuto
              arrivato: true
            }));
            
            // Batch update per evitare ri-render multipli
            setProdottiVerifica(prodottiConStato);
            setAnalisiResult(analisi);
            
            // Confronta con ordine
            confrontaDDTConOrdine(analisi);
            
            // Usa setTimeout per evitare sfarfallio durante il cambio step
            setTimeout(() => {
              setDdtStep(2); // Passa a verifica
              setAnalyzingDDT(false);
              toast.success(`${prodottiConStato.length} prodotti trovati`);
            }, 100);
          } else {
            setAnalyzingDDT(false);
            toast.error(res.data.detail || 'Errore durante l\'analisi');
          }
        } catch (error) {
          console.error('Errore analisi:', error);
          setAnalyzingDDT(false);
          toast.error(error.response?.data?.detail || 'Errore durante l\'analisi');
        }
      };
      
      reader.readAsDataURL(selectedFile.file);
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore durante la lettura del file');
      setAnalyzingDDT(false);
    }
  };

  // Confronta DDT ricevuto con ordine originale
  const confrontaDDTConOrdine = (analisi) => {
    if (!selectedDDT || !selectedDDT.prodotti_attesi) return;
    
    const prodottiOrdinati = selectedDDT.prodotti_attesi || [];
    const prodottiRicevuti = analisi.prodotti || [];
    
    const confrontoItems = [];
    let totaleOrdinato = 0;
    let totaleRicevuto = 0;
    
    // Normalizza nome per confronto
    const normalizzaNome = (nome) => {
      return (nome || '').toUpperCase()
        .replace(/\s+IT$/i, '')
        .replace(/\s+PERU$/i, '')
        .replace(/\s+/g, ' ')
        .trim();
    };
    
    // Mappa prodotti ricevuti per nome normalizzato
    const ricevutiMap = {};
    prodottiRicevuti.forEach(p => {
      const nomeNorm = normalizzaNome(p.nome);
      ricevutiMap[nomeNorm] = p;
      totaleRicevuto += parseFloat(p.importo || 0) || (parseFloat(p.quantita || 0) * parseFloat(p.prezzo_unitario || 0));
    });
    
    // Verifica ogni prodotto ordinato
    prodottiOrdinati.forEach(ordinato => {
      const nomeNorm = normalizzaNome(ordinato.nome);
      const ricevuto = ricevutiMap[nomeNorm];
      
      totaleOrdinato += parseFloat(ordinato.importo || 0) || (parseFloat(ordinato.quantita || 0) * parseFloat(ordinato.prezzo_unitario || 0));
      
      if (ricevuto) {
        // Prodotto trovato - verifica variazioni
        const variazionePrezzo = ordinato.prezzo_unitario && ricevuto.prezzo_unitario
          ? ((ricevuto.prezzo_unitario - ordinato.prezzo_unitario) / ordinato.prezzo_unitario * 100)
          : 0;
        
        confrontoItems.push({
          nome: ordinato.nome,
          status: Math.abs(variazionePrezzo) > 5 ? 'variazione' : 'ok',
          ordinato: { quantita: ordinato.quantita, unita: ordinato.unita_misura, prezzo: ordinato.prezzo_unitario },
          ricevuto: { quantita: ricevuto.quantita, unita: ricevuto.unita_misura, prezzo: ricevuto.prezzo_unitario },
          variazionePrezzo: variazionePrezzo
        });
        
        // Rimuovi dalla mappa
        delete ricevutiMap[nomeNorm];
      } else {
        // Prodotto ordinato ma NON ricevuto
        confrontoItems.push({
          nome: ordinato.nome,
          status: 'mancante',
          ordinato: { quantita: ordinato.quantita, unita: ordinato.unita_misura, prezzo: ordinato.prezzo_unitario },
          ricevuto: null
        });
      }
    });
    
    // Prodotti ricevuti ma NON ordinati (extra)
    Object.values(ricevutiMap).forEach(ricevuto => {
      confrontoItems.push({
        nome: ricevuto.nome,
        status: 'extra',
        ordinato: null,
        ricevuto: { quantita: ricevuto.quantita, unita: ricevuto.unita_misura, prezzo: ricevuto.prezzo_unitario }
      });
    });
    
    setConfrontoOrdine({
      items: confrontoItems,
      totaleOrdinato,
      totaleRicevuto
    });
  };

  // Reset modal DDT
  const resetDDTModal = () => {
    setSelectedDDT(null);
    setSelectedFile(null);
    setPreviewUrl(null);
    setDdtStep(1);
    setProdottiVerifica([]);
    setConfrontoOrdine(null);
    setAnalisiResult(null);
  };

  // Salva DDT verificato
  const salvaDDTVerificato = async () => {
    if (!selectedDDT || prodottiVerifica.length === 0) return;
    
    const prodottiDaSalvare = prodottiVerifica.filter(p => p.arrivato);
    if (prodottiDaSalvare.length === 0) {
      toast.error('Seleziona almeno un prodotto ricevuto');
      return;
    }
    
    setLoading(true);
    try {
      // Aggiorna DDT con i prodotti ricevuti
      const updateData = {
        status: 'ricevuto',
        data_ricezione: new Date().toISOString(),
        prodotti: prodottiDaSalvare.map(p => ({
          nome: p.nome,
          quantita: p.quantita,
          unita_misura: p.unita_misura,
          prezzo_unitario: p.prezzo_unitario,
          importo: p.importo,
          lotto: p.lotto,
          scadenza: p.scadenza,
          stato_consegna: p.stato
        })),
        fornitore: analisiResult?.fornitore || selectedDDT.fornitore,
        numero_ddt: analisiResult?.numero_ddt,
        data_ddt: analisiResult?.data_ddt
      };
      
      await axios.put(`${API}/gestione/ddt/${selectedDDT.id}`, updateData, {
        headers: getAuthHeaders()
      });
      
      toast.success('DDT salvato con successo');
      resetDDTModal();
      loadDDTAttesa();
    } catch (error) {
      console.error('Errore salvataggio:', error);
      toast.error('Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const analizzaDDT = async (ddt, imageUrl) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/gestione/ddt/${ddt.id}/analizza-ai?immagine_url=${encodeURIComponent(imageUrl)}`, {}, {
        headers: getAuthHeaders()
      });
      
      setAnalisiResult(res.data);
      setShowAnalisiModal(true);
      toast.success('Analisi completata');
    } catch (error) {
      console.error('Errore analisi:', error);
      toast.error('Errore durante l\'analisi del DDT');
    } finally {
      setLoading(false);
    }
  };

  const updateFornitore = async (fornitore, updates) => {
    try {
      await axios.put(`${API}/gestione/fornitori/${encodeURIComponent(fornitore.nome)}`, updates, {
        headers: getAuthHeaders()
      });
      toast.success('Fornitore aggiornato');
      loadFornitori();
      setEditingFornitore(null);
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore aggiornamento fornitore');
    }
  };

  // Gestione foto lista della spesa
  const handleFotoListaSpesa = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Converti in base64
    const reader = new FileReader();
    reader.onload = async () => {
      const base64Image = reader.result.split(',')[1];
      
      setLoadingFoto(true);
      setShowFotoAnalisiModal(true);
      setShowImportMenu(false);
      
      try {
        const res = await axios.post(`${API}/gestione/ordini/analizza-foto-lista`, {
          image_base64: base64Image
        }, {
          headers: getAuthHeaders()
        });
        
        // Mappa i prodotti per il frontend (nome -> nome_originale)
        const prodottiMappati = (res.data.prodotti || []).map(p => ({
          ...p,
          nome_originale: p.nome || p.nome_originale || '',
          abbinamenti: p.suggerimenti_fornitori || [],
          abbinamento_selezionato: null,
          confermato: false,
          editing: false,
          editValue: p.nome || p.nome_originale || ''
        }));
        
        setFotoAnalisiResult({
          ...res.data,
          prodotti: prodottiMappati
        });
        toast.success('Lista analizzata con successo!');
      } catch (error) {
        console.error('Errore analisi foto:', error);
        toast.error('Errore durante l\'analisi della foto');
        setShowFotoAnalisiModal(false);
      } finally {
        setLoadingFoto(false);
      }
    };
    reader.readAsDataURL(file);
    
    // Reset input
    e.target.value = '';
  };

  // Import da file di testo
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setTestoImport(reader.result);
      setShowImportTestoModal(true);
      setShowImportMenu(false);
    };
    reader.readAsText(file);
    
    e.target.value = '';
  };

  // Analizza testo importato
  const handleAnalizzaTesto = async () => {
    if (!testoImport.trim()) {
      toast.error('Inserisci del testo da analizzare');
      return;
    }

    setLoadingFoto(true);
    setShowImportTestoModal(false);
    setShowFotoAnalisiModal(true);

    try {
      const res = await axios.post(`${API}/gestione/ordini/importa-lista-testo`, {
        testo: testoImport
      }, {
        headers: getAuthHeaders()
      });
      
      // Mappa i prodotti per il frontend (nome -> nome_originale)
      const prodottiMappati = (res.data.prodotti || []).map(p => ({
        ...p,
        nome_originale: p.nome || p.nome_originale || '',
        abbinamenti: p.suggerimenti_fornitori || [],
        abbinamento_selezionato: null,
        confermato: false,
        editing: false,
        editValue: p.nome || p.nome_originale || ''
      }));
      
      setFotoAnalisiResult({
        ...res.data,
        prodotti: prodottiMappati
      });
      setTestoImport('');
      toast.success('Lista importata con successo!');
    } catch (error) {
      console.error('Errore import testo:', error);
      toast.error('Errore durante l\'importazione');
      setShowFotoAnalisiModal(false);
    } finally {
      setLoadingFoto(false);
    }
  };

  // Aggiungi prodotti dalla foto analizzata
  const aggiungiProdottoDaFoto = async (prodotto) => {
    try {
      await axios.post(`${API}/gestione/ordini/lista-spesa`, {
        nome: prodotto.nome,
        quantita: prodotto.quantita || 1,
        unita_misura: prodotto.unita_misura || 'pz',
        fornitore_suggerito: prodotto.fornitore_suggerito,
        prezzo_stimato: prodotto.prezzo_stimato || 0
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success(`${prodotto.nome} aggiunto alla lista`);
      
      // Ricarica la lista dal database
      await loadListaSpesa();
      
      // Aggiorna la lista locale
      setFotoAnalisiResult(prev => ({
        ...prev,
        prodotti: prev.prodotti.map(p => 
          p.nome === prodotto.nome ? { ...p, aggiunto: true } : p
        )
      }));
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore aggiunta prodotto');
    }
  };

  // Aggiungi tutti i prodotti dalla foto
  const aggiungiTuttiProdottiFoto = async () => {
    if (!fotoAnalisiResult?.prodotti) return;
    
    const prodottiNonAggiunti = fotoAnalisiResult.prodotti.filter(p => !p.aggiunto);
    
    for (const prodotto of prodottiNonAggiunti) {
      await aggiungiProdottoDaFoto(prodotto);
    }
    
    toast.success('Tutti i prodotti aggiunti!');
  };

  // Seleziona un abbinamento per un prodotto dalla foto
  const selezionaAbbinamento = (prodottoIdx, abbinamento) => {
    setFotoAnalisiResult(prev => ({
      ...prev,
      prodotti: prev.prodotti.map((p, idx) => 
        idx === prodottoIdx 
          ? { ...p, abbinamento_selezionato: abbinamento }
          : p
      )
    }));
  };

  // Modifica il nome di un prodotto estratto dalla foto
  const modificaNomeProdotto = async (prodottoIdx, nuovoNome) => {
    if (!nuovoNome || nuovoNome.trim().length < 2) return;
    
    // Cerca abbinamenti per il nuovo nome
    try {
      const res = await axios.get(`${API}/gestione/ordini/cerca-prodotti-fatture?q=${encodeURIComponent(nuovoNome)}`, {
        headers: getAuthHeaders()
      });
      
      setFotoAnalisiResult(prev => ({
        ...prev,
        prodotti: prev.prodotti.map((p, idx) => 
          idx === prodottoIdx 
            ? { 
                ...p, 
                nome_originale: nuovoNome.toUpperCase(),
                abbinamenti: res.data.abbinamenti || [],
                abbinamento_selezionato: null,
                editing: false
              }
            : p
        )
      }));
    } catch (error) {
      console.error('Errore ricerca prodotti:', error);
      toast.error('Errore nella ricerca');
    }
  };

  // Toggle modalità editing per un prodotto
  const toggleEditingProdotto = (prodottoIdx) => {
    setFotoAnalisiResult(prev => ({
      ...prev,
      prodotti: prev.prodotti.map((p, idx) => 
        idx === prodottoIdx 
          ? { ...p, editing: !p.editing, editValue: p.nome_originale }
          : p
      )
    }));
  };

  // Aggiorna valore in editing
  const updateEditValue = (prodottoIdx, value) => {
    setFotoAnalisiResult(prev => ({
      ...prev,
      prodotti: prev.prodotti.map((p, idx) => 
        idx === prodottoIdx 
          ? { ...p, editValue: value }
          : p
      )
    }));
  };

  // Aggiungi nuovo prodotto manualmente alla lista foto
  const aggiungiProdottoManuale = () => {
    setFotoAnalisiResult(prev => ({
      ...prev,
      prodotti: [
        ...(prev?.prodotti || []),
        {
          nome_originale: '',
          quantita: 1,
          unita_misura: 'pz',
          abbinamenti: [],
          abbinamento_selezionato: null,
          confermato: false,
          editing: true,
          editValue: '',
          isNew: true
        }
      ]
    }));
    
    // Scroll automatico verso il fondo dopo l'aggiunta
    setTimeout(() => {
      const modalContent = document.querySelector('[data-modal-content="abbina-prodotti"]');
      if (modalContent) {
        modalContent.scrollTo({ top: modalContent.scrollHeight, behavior: 'smooth' });
      }
    }, 100);
  };

  // Rimuovi prodotto dalla lista foto
  const rimuoviProdottoFoto = (prodottoIdx) => {
    setFotoAnalisiResult(prev => ({
      ...prev,
      prodotti: prev.prodotti.filter((_, idx) => idx !== prodottoIdx)
    }));
  };

  // Conferma l'abbinamento e aggiungi alla lista
  const confermaAbbinamento = async (prodottoIdx) => {
    const prodotto = fotoAnalisiResult.prodotti[prodottoIdx];
    if (!prodotto.abbinamento_selezionato) return;

    const abb = prodotto.abbinamento_selezionato;
    
    // Usa i valori confermati dall'utente, altrimenti quelli originali dalla foto
    // PRIORITÀ: utente > foto originale > abbinamento > default
    const quantitaFinale = prodotto.quantita_conferma ?? prodotto.quantita ?? 1;
    const unitaFinale = prodotto.unita_conferma ?? prodotto.unita_misura ?? abb.unita_misura ?? 'kg';
    
    try {
      await axios.post(`${API}/gestione/ordini/lista-spesa`, {
        nome: abb.descrizione,
        quantita: quantitaFinale,
        unita_misura: unitaFinale,
        fornitore_nome: abb.fornitore,
        prezzo_stimato: abb.prezzo || 0
      }, {
        headers: getAuthHeaders()
      });
      
      // Marca come confermato
      setFotoAnalisiResult(prev => ({
        ...prev,
        prodotti: prev.prodotti.map((p, idx) => 
          idx === prodottoIdx 
            ? { ...p, confermato: true }
            : p
        )
      }));
      
      // Ricarica la lista
      await loadListaSpesa();
      
      toast.success(`${abb.descrizione} (${quantitaFinale} ${unitaFinale}) aggiunto alla lista`);
    } catch (error) {
      console.error('Errore conferma abbinamento:', error);
      toast.error('Errore durante l\'aggiunta');
    }
  };

  // Raggruppa lista spesa per fornitore (preview)
  const listaPerFornitore = listaSpesa.reduce((acc, item) => {
    const fornitore = item.fornitore_nome || 'Altro';
    if (!acc[fornitore]) {
      acc[fornitore] = [];
    }
    acc[fornitore].push(item);
    return acc;
  }, {});

  const getStatusBadge = (status) => {
    const styles = {
      'da_inviare': 'bg-yellow-100 text-yellow-700',
      'inviato': 'bg-blue-100 text-blue-700',
      'confermato': 'bg-green-100 text-green-700',
      'consegnato': 'bg-emerald-100 text-emerald-700',
      'in_attesa': 'bg-orange-100 text-orange-700'
    };
    const labels = {
      'da_inviare': 'Da Inviare',
      'inviato': 'Inviato',
      'confermato': 'Confermato',
      'consegnato': 'Consegnato',
      'in_attesa': 'In Attesa'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="space-y-4 max-w-full">
      {/* Overlay di loading durante analisi DDT - STABILE senza sfarfallio */}
      {analyzingDDT && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[200]">
          <div className="bg-slate-800 rounded-2xl p-8 text-center max-w-sm mx-4">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h3 className="text-white text-lg font-bold mb-2">Analisi in corso...</h3>
            <p className="text-slate-400 text-sm">Stiamo elaborando il documento DDT</p>
          </div>
        </div>
      )}
      
      {/* Header - Mobile Dark Style like F&B */}
      <div className="space-y-3">
        <div className="lg:block hidden">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Gestione Ordini</h1>
          <p className="text-sm text-gray-500">Crea liste della spesa e gestisci ordini ai fornitori</p>
        </div>
        
        {/* Tabs - Mobile Dark Style */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {[
            { id: 'lista_spesa', label: 'Lista', icon: ShoppingCart, count: listaSpesa.length },
            { id: 'ordini_fornitore', label: 'Ordini', icon: Send, count: ordiniFornitore.filter(o => o.status === 'da_inviare').length },
            { id: 'ddt_attesa', label: 'DDT', icon: Package, count: ddtAttesa.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-700 lg:bg-gray-100 text-slate-300 lg:text-gray-600'
              }`}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-blue-500/30 text-blue-300 lg:bg-blue-100 lg:text-blue-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
          
          {/* Bottone Fornitori */}
          <button
            onClick={() => setShowFornitoriModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-emerald-600/20 text-emerald-400 lg:bg-emerald-100 lg:text-emerald-700 whitespace-nowrap"
          >
            <Phone className="h-3.5 w-3.5" />
            Fornitori
          </button>
        </div>
      </div>

      {/* TAB: Lista della Spesa */}
      {activeTab === 'lista_spesa' && (
        <div className="space-y-3">
          {/* Action Buttons - Import Menu & Search */}
          <div className="flex gap-2">
            {/* Import Button with Dropdown Menu */}
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setShowImportMenu(!showImportMenu)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium text-sm active:scale-95 transition-transform"
              >
                <Camera className="h-4 w-4" />
                <span className="hidden sm:inline">Importa</span>
              </button>
              
              {/* Dropdown Menu */}
              {showImportMenu && (
                <div className="absolute left-0 top-full mt-2 w-52 bg-slate-800/70 backdrop-blur-sm lg:bg-white border border-slate-700 lg:border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
                  {/* Scatta Foto */}
                  <label className="flex items-center gap-3 px-4 py-3 hover:bg-slate-700 lg:hover:bg-gray-100 cursor-pointer transition-colors">
                    <Camera className="h-4 w-4 text-purple-400" />
                    <div>
                      <p className="text-sm font-medium text-white lg:text-gray-900">Scatta Foto</p>
                      <p className="text-xs text-slate-400 lg:text-gray-500">Usa la fotocamera</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={handleFotoListaSpesa}
                    />
                  </label>
                  
                  {/* Importa da Galleria */}
                  <label className="flex items-center gap-3 px-4 py-3 hover:bg-slate-700 lg:hover:bg-gray-100 cursor-pointer transition-colors border-t border-slate-700 lg:border-gray-200">
                    <Upload className="h-4 w-4 text-blue-400" />
                    <div>
                      <p className="text-sm font-medium text-white lg:text-gray-900">Da Galleria</p>
                      <p className="text-xs text-slate-400 lg:text-gray-500">Carica un'immagine</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFotoListaSpesa}
                    />
                  </label>
                  
                  {/* Importa da File */}
                  <label className="flex items-center gap-3 px-4 py-3 hover:bg-slate-700 lg:hover:bg-gray-100 cursor-pointer transition-colors border-t border-slate-700 lg:border-gray-200">
                    <FileText className="h-4 w-4 text-emerald-400" />
                    <div>
                      <p className="text-sm font-medium text-white lg:text-gray-900">Da File</p>
                      <p className="text-xs text-slate-400 lg:text-gray-500">Importa .txt</p>
                    </div>
                    <input
                      type="file"
                      accept=".txt,.csv,text/*"
                      className="hidden"
                      onChange={handleImportFile}
                    />
                  </label>
                  
                  {/* Inserisci Testo */}
                  <button
                    onClick={() => {
                      setShowImportMenu(false);
                      setShowImportTestoModal(true);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-700 lg:hover:bg-gray-100 transition-colors border-t border-slate-700 lg:border-gray-200 text-left"
                  >
                    <Edit className="h-4 w-4 text-amber-400" />
                    <div>
                      <p className="text-sm font-medium text-white lg:text-gray-900">Scrivi Lista</p>
                      <p className="text-xs text-slate-400 lg:text-gray-500">Inserisci manualmente</p>
                    </div>
                  </button>
                </div>
              )}
            </div>
            
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Cerca prodotto..."
                value={searchProdotto}
                onChange={(e) => setSearchProdotto(e.target.value)}
                className="w-full pl-10 pr-3 h-10 rounded-xl bg-slate-800/70 backdrop-blur-sm lg:bg-white lg:border lg:border-gray-200 text-white lg:text-gray-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Suggerimenti ricerca */}
          {showSuggerimenti && suggerimenti.length > 0 && (
            <div className="bg-slate-800/70 backdrop-blur-sm lg:bg-white border border-slate-700 lg:border-gray-200 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
              {suggerimenti.map((prodotto, idx) => (
                <div key={idx} className="border-b border-slate-700 lg:border-gray-100 last:border-0">
                  {prodotto.fornitori.map((fornitore, fIdx) => (
                    <div 
                      key={fIdx}
                      className={`px-3 py-2.5 cursor-pointer transition-colors ${
                        fornitore.is_migliore 
                          ? 'bg-emerald-900/30 lg:bg-emerald-50 border-l-4 border-emerald-500' 
                          : 'active:bg-slate-700 lg:hover:bg-blue-50'
                      }`}
                      onClick={() => handleSelectProdotto(prodotto, fornitore)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-white lg:text-gray-900 font-medium uppercase truncate">{prodotto.nome}</p>
                            {fornitore.is_migliore && (
                              <span className="px-1.5 py-0.5 bg-emerald-500 text-white text-[9px] font-bold rounded uppercase">
                                Miglior Prezzo
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-[11px] text-slate-400 lg:text-gray-500 mt-0.5">
                            <span className={`font-medium ${fornitore.is_migliore ? 'text-emerald-400 lg:text-emerald-600' : 'text-blue-400 lg:text-blue-600'}`}>
                              {fornitore.nome}
                            </span>
                            <span>•</span>
                            <span>€{fornitore.ultimo_prezzo}/{fornitore.unita_misura}</span>
                            {fornitore.diff_vs_migliore_perc > 0 && (
                              <span className="text-red-400 lg:text-red-500">
                                (+{fornitore.diff_vs_migliore_perc}%)
                              </span>
                            )}
                            {fornitore.is_migliore && fornitore.risparmio_vs_max > 0 && (
                              <span className="text-emerald-400 lg:text-emerald-600">
                                Risparmi €{fornitore.risparmio_vs_max}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className={`text-base font-bold ${
                          fornitore.is_migliore 
                            ? 'text-emerald-400 lg:text-emerald-600' 
                            : 'text-slate-300 lg:text-gray-600'
                        }`}>
                          €{fornitore.ultimo_prezzo}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Nessun risultato */}
          {showSuggerimenti && suggerimenti.length === 0 && searchProdotto.length >= 2 && (
            <div className="bg-slate-800/70 backdrop-blur-sm lg:bg-white border border-slate-700 lg:border-gray-200 rounded-xl p-4 text-center">
              <Search className="h-6 w-6 text-slate-500 mx-auto mb-2" />
              <p className="text-slate-300 lg:text-gray-500 text-sm">Nessun prodotto trovato</p>
            </div>
          )}

          {/* Lista della spesa raggruppata per fornitore */}
          {listaSpesa.length > 0 ? (
            <div className="space-y-3">
              {Object.entries(listaPerFornitore).map(([fornitore, items]) => (
                <div key={fornitore} className="bg-slate-800/70 backdrop-blur-sm lg:bg-white/90 backdrop-blur-sm rounded-xl overflow-hidden border border-slate-700 lg:border-gray-200">
                  {/* Header fornitore */}
                  <div className="px-3 py-2 bg-slate-700/50 lg:bg-gray-50 flex items-center gap-2">
                    <Package className="h-4 w-4 text-blue-400 lg:text-blue-500" />
                    <span className="text-sm font-medium text-white lg:text-gray-700">{fornitore}</span>
                    <span className="text-[10px] bg-blue-500/30 lg:bg-blue-100 text-blue-300 lg:text-blue-600 px-2 py-0.5 rounded-full">
                      {items.length}
                    </span>
                  </div>
                  
                  {/* Lista prodotti */}
                  <div className="divide-y divide-slate-700/50 lg:divide-gray-100">
                    {items.map((item) => (
                      <div key={item.id} className="px-3 py-2.5 flex items-center justify-between">
                        <div 
                          className="flex-1 min-w-0 cursor-pointer active:opacity-70"
                          onClick={() => apriModificaItem(item)}
                        >
                          <p className="text-sm font-medium text-white lg:text-gray-900 uppercase truncate">{item.nome}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-xs text-slate-400 lg:text-gray-500">{item.quantita} {item.unita_misura}</span>
                            <span className="text-slate-600">×</span>
                            <span className="text-xs text-amber-400 font-medium">€{item.prezzo_stimato}</span>
                            <span className="text-slate-600">=</span>
                            <span className="text-xs text-emerald-400 lg:text-blue-600 font-bold">€{(item.quantita * item.prezzo_stimato).toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button 
                            onClick={() => apriModificaItem(item)}
                            className="p-2 text-slate-500 hover:text-blue-400 active:bg-blue-500/20 rounded-lg"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => rimuoviDallaLista(item.id)}
                            className="p-2 text-slate-500 hover:text-red-400 active:bg-red-500/20 rounded-lg"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Totale e azioni */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-4 text-white">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-blue-100 text-sm">Totale stimato</p>
                    <p className="text-2xl font-bold">
                      €{listaSpesa.reduce((sum, item) => sum + (item.quantita * item.prezzo_stimato), 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-blue-100 text-sm">{listaSpesa.length} prodotti</p>
                    <p className="text-lg font-medium">{Object.keys(listaPerFornitore).length} fornitori</p>
                  </div>
                </div>
                <Button 
                  onClick={generaOrdiniPerFornitore}
                  disabled={loading}
                  className="w-full bg-white text-blue-600 hover:bg-blue-50 rounded-xl h-12 font-semibold"
                >
                  {loading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Genera Ordini per Fornitore
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-10 bg-slate-800/70 backdrop-blur-sm lg:bg-white/90 backdrop-blur-sm rounded-xl border border-slate-700 lg:border-gray-200">
              <div className="w-14 h-14 bg-slate-700 lg:bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                <ShoppingCart className="h-7 w-7 text-blue-400" />
              </div>
              <p className="text-slate-300 lg:text-gray-500 text-sm mb-1">Lista vuota</p>
              <p className="text-xs text-slate-500 lg:text-gray-400">Cerca o scatta una foto per iniziare</p>
            </div>
          )}
        </div>
      )}

      {/* TAB: Ordini per Fornitore */}
      {activeTab === 'ordini_fornitore' && (
        <div className="space-y-4">
          {/* Tasto Nuova Bolla - apre direttamente Nuova Bolla in Gestione Bolla */}
          <button
            onClick={() => setCurrentPage && setCurrentPage('bolle', { openNuovaBolla: true })}
            className="w-full py-4 rounded-2xl bg-purple-500 hover:bg-purple-600 text-white font-semibold flex items-center justify-center gap-2 shadow-lg transition-all"
            data-testid="btn-nuova-bolla"
          >
            <Plus className="h-5 w-5" />
            Nuova Bolla
          </button>
          
          {ordiniFornitore.length > 0 ? (
            ordiniFornitore.map((ordine) => (
              <Card key={ordine.id} className="border-gray-100 overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-gray-900">{ordine.fornitore_nome}</h3>
                        {getStatusBadge(ordine.stato)}
                      </div>
                      <p className="text-sm text-gray-500">
                        {ordine.prodotti?.length || 0} prodotti • Creato: {new Date(ordine.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {ordine.whatsapp_link && ordine.stato === 'da_inviare' && (
                        <Button 
                          onClick={() => inviaWhatsApp(ordine)}
                          className="bg-green-500 hover:bg-green-600 rounded-xl"
                        >
                          <MessageCircle className="h-4 w-4 mr-2" />
                          WhatsApp
                        </Button>
                      )}
                      <button
                        onClick={() => eliminaOrdineFornitore(ordine.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                        title="Elimina ordine"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Lista prodotti */}
                  <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                    {ordine.prodotti?.map((p, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-700">{p.nome}</span>
                        <span className="font-medium text-gray-900">{p.quantita} {p.unita_misura}</span>
                      </div>
                    ))}
                  </div>

                  {!ordine.whatsapp_link && (
                    <div className="mt-3 flex items-center gap-2 text-amber-600 text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      Numero WhatsApp non configurato per questo fornitore
                    </div>
                  )}
                </div>
              </Card>
            ))
          ) : (
            <div className="text-center py-12 bg-white/90 backdrop-blur-sm rounded-2xl">
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Send className="h-8 w-8 text-blue-400" />
              </div>
              <p className="text-gray-500">Nessun ordine da inviare</p>
              <p className="text-sm text-gray-400 mt-1">Crea una lista della spesa per generare ordini</p>
            </div>
          )}
        </div>
      )}

      {/* TAB: DDT in Attesa */}
      {activeTab === 'ddt_attesa' && (
        <div className="space-y-4">
          {ddtAttesa.length > 0 ? (
            ddtAttesa.map((ddt) => (
              <Card key={ddt.id} className="border-orange-100 overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-gray-900">DDT #{ddt.numero}</h3>
                        {getStatusBadge(ddt.status)}
                      </div>
                      <p className="text-sm text-gray-500">
                        {ddt.fornitore} • {ddt.prodotti_attesi?.length || 0} prodotti attesi
                        {ddt.data_creazione && <span> • Creato: {ddt.data_creazione}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        onClick={() => setSelectedDDT(ddt)}
                        className="bg-blue-600 hover:bg-blue-700 rounded-xl"
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        Carica DDT
                      </Button>
                      <button
                        onClick={() => eliminaDDT(ddt.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                        title="Elimina DDT"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Prodotti attesi */}
                  <div className="bg-orange-50 rounded-xl p-3 space-y-2">
                    <p className="text-xs font-medium text-orange-700 mb-2">Prodotti ordinati:</p>
                    {ddt.prodotti_attesi?.map((p, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-700">{p.nome}</span>
                        <span className="font-medium text-gray-900">{p.quantita} {p.unita_misura}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className="text-center py-12 bg-white/90 backdrop-blur-sm rounded-2xl">
              <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Package className="h-8 w-8 text-orange-400" />
              </div>
              <p className="text-gray-500">Nessun DDT in attesa</p>
              <p className="text-sm text-gray-400 mt-1">I DDT verranno creati automaticamente quando generi gli ordini</p>
            </div>
          )}
        </div>
      )}

      {/* Modal Modifica Item Lista Spesa */}
      {showEditItemModal && editingItem && (
        <div className="fixed inset-0 bg-black/70 flex items-end lg:items-center justify-center z-[60]">
          <div className="bg-slate-900/95 backdrop-blur-sm lg:bg-white rounded-t-3xl lg:rounded-2xl w-full lg:max-w-md max-h-[80vh] flex flex-col mb-16 lg:mb-0">
            {/* Header */}
            <div className="p-4 border-b border-slate-700 lg:border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white lg:text-gray-900">✏️ Modifica Prodotto</h2>
                <button 
                  onClick={() => { setShowEditItemModal(false); setEditingItem(null); }} 
                  className="p-2 bg-slate-800 lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 rounded-xl"
                >
                  <X className="h-5 w-5 text-white lg:text-gray-600" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Nome prodotto */}
              <div>
                <label className="text-sm font-medium text-slate-300 lg:text-gray-700 mb-1.5 block">Nome prodotto</label>
                <Input
                  value={editItemData.nome}
                  onChange={(e) => setEditItemData({...editItemData, nome: e.target.value})}
                  className="h-11 rounded-xl bg-slate-800/70 lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900 uppercase"
                />
              </div>

              {/* Quantità e Unità di misura */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-300 lg:text-gray-700 mb-1.5 block">Quantità</label>
                  <Input
                    type="number"
                    value={editItemData.quantita}
                    onChange={(e) => setEditItemData({...editItemData, quantita: e.target.value})}
                    className="h-11 text-center font-bold rounded-xl bg-slate-800/70 lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 lg:text-gray-700 mb-1.5 block">Unità</label>
                  <div className="grid grid-cols-5 gap-1">
                    {['kg', 'pz', 'ct', 'lt', 'cs'].map((unit) => (
                      <button
                        key={unit}
                        onClick={() => setEditItemData({...editItemData, unita_misura: unit})}
                        className={`py-2 rounded-lg font-semibold text-xs uppercase transition-all ${
                          editItemData.unita_misura === unit 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-slate-800 lg:bg-gray-100 text-slate-300 lg:text-gray-600'
                        }`}
                      >
                        {unit}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Prezzo */}
              <div>
                <label className="text-sm font-medium text-slate-300 lg:text-gray-700 mb-1.5 block">Prezzo unitario (€)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={editItemData.prezzo_stimato}
                  onChange={(e) => setEditItemData({...editItemData, prezzo_stimato: e.target.value})}
                  className="h-11 text-center font-bold rounded-xl bg-slate-800/70 lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                />
              </div>

              {/* Totale stimato */}
              {editItemData.quantita && editItemData.prezzo_stimato && (
                <div className="bg-slate-800/70 lg:bg-gray-50 rounded-xl p-3 flex items-center justify-between border border-slate-700 lg:border-transparent">
                  <span className="text-slate-400 lg:text-gray-600 text-sm">Totale stimato</span>
                  <span className="text-xl font-bold text-white lg:text-gray-900">
                    €{(parseFloat(editItemData.quantita) * parseFloat(editItemData.prezzo_stimato)).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            {/* Footer con bottoni */}
            <div className="p-4 border-t border-slate-700 lg:border-gray-100 flex-shrink-0 flex gap-2">
              <Button 
                onClick={() => {
                  rimuoviDallaLista(editingItem.id);
                  setShowEditItemModal(false);
                  setEditingItem(null);
                }}
                variant="outline"
                className="flex-1 rounded-xl h-12 border-red-500/50 text-red-400 hover:bg-red-500/20"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Elimina
              </Button>
              <Button 
                onClick={salvaModificheItem}
                className="flex-1 bg-blue-600 hover:bg-blue-700 rounded-xl h-12 font-semibold"
              >
                <Check className="h-5 w-5 mr-2" />
                Salva
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Quantità - Appare quando si seleziona un prodotto */}
      {showQuantitaModal && selectedProdotto && (
        <div className="fixed inset-0 bg-black/70 flex items-end lg:items-center justify-center z-[60]">
          {/* Mobile: Bottom sheet dark / Desktop: Modal centered */}
          <div className="bg-slate-900/95 backdrop-blur-sm lg:bg-white/90 backdrop-blur-sm rounded-t-3xl lg:rounded-2xl w-full lg:max-w-md max-h-[75vh] lg:max-h-[85vh] flex flex-col mb-16 lg:mb-0">
            {/* Header */}
            <div className="p-4 border-b border-slate-700 lg:border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white lg:text-gray-900">Aggiungi alla Lista</h2>
                <button 
                  onClick={chiudiQuantitaModal} 
                  className="p-2 bg-slate-800 lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 rounded-xl transition-colors"
                >
                  <X className="h-5 w-5 text-white lg:text-gray-600" />
                </button>
              </div>
            </div>
            
            {/* Content scrollabile */}
            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              {/* Prodotto selezionato */}
              <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 lg:from-blue-50 lg:to-purple-50 rounded-xl p-3 border border-blue-500/30 lg:border-transparent">
                <p className="font-bold text-white lg:text-gray-900 text-sm">{selectedProdotto.prodotto.nome}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-blue-400 lg:text-blue-600 font-medium">{selectedProdotto.fornitore.nome}</span>
                  <span className="text-lg font-bold text-blue-400 lg:text-blue-600">€{selectedProdotto.fornitore.ultimo_prezzo}</span>
                </div>
              </div>

              {/* Quantità */}
              <div>
                <label className="text-sm font-medium text-slate-300 lg:text-gray-700 mb-1.5 block">Quantità</label>
                <Input
                  type="number"
                  placeholder="Es. 5"
                  value={quantita}
                  onChange={(e) => setQuantita(e.target.value)}
                  className="h-12 text-lg font-bold text-center rounded-xl bg-slate-800/70 backdrop-blur-sm lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900 placeholder-slate-500"
                  autoFocus
                />
              </div>

              {/* Unità di misura */}
              <div>
                <label className="text-sm font-medium text-slate-300 lg:text-gray-700 mb-1.5 block">Unità di misura</label>
                <div className="grid grid-cols-5 gap-2">
                  {['kg', 'pz', 'ct', 'lt', 'cs'].map((unit) => (
                    <button
                      key={unit}
                      onClick={() => setUnitaMisura(unit)}
                      className={`py-2.5 rounded-xl font-semibold text-sm uppercase transition-all ${
                        unitaMisura === unit 
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' 
                          : 'bg-slate-800 lg:bg-gray-100 text-slate-300 lg:text-gray-600 hover:bg-slate-700 lg:hover:bg-gray-200'
                      }`}
                    >
                      {unit}
                    </button>
                  ))}
                </div>
              </div>

              {/* Riepilogo prezzo stimato */}
              {quantita && parseFloat(quantita) > 0 && (
                <div className="bg-slate-800/70 backdrop-blur-sm lg:bg-gray-50 rounded-xl p-3 flex items-center justify-between border border-slate-700 lg:border-transparent">
                  <span className="text-slate-400 lg:text-gray-600 text-sm">Totale stimato</span>
                  <span className="text-xl font-bold text-white lg:text-gray-900">
                    €{(parseFloat(quantita) * selectedProdotto.fornitore.ultimo_prezzo).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            {/* Bottone CONFERMA - sempre visibile in fondo */}
            <div className="p-4 border-t border-slate-700 lg:border-gray-100 flex-shrink-0 bg-slate-900/95 lg:bg-white">
              <Button 
                onClick={aggiungiAllaLista}
                disabled={!quantita || parseFloat(quantita) <= 0}
                className="w-full bg-blue-600 hover:bg-blue-700 rounded-xl h-12 text-base font-semibold disabled:opacity-50"
              >
                <Check className="h-5 w-5 mr-2" />
                Conferma {quantita && parseFloat(quantita) > 0 ? `${quantita} ${unitaMisura}` : ''}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Carica DDT - Step Flow come GestioneBollePage */}
      {selectedDDT && (
        <div className="fixed inset-0 bg-black/50 flex items-end lg:items-center justify-center z-50 p-4">
          <div className="bg-slate-900/95 lg:bg-white/95 backdrop-blur-sm rounded-t-3xl lg:rounded-2xl w-full lg:max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header con info ordine */}
            <div className="p-5 border-b border-slate-700 lg:border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white lg:text-gray-900">
                    {ddtStep === 1 && 'Carica DDT'}
                    {ddtStep === 2 && 'Verifica Prodotti'}
                  </h2>
                  <p className="text-sm text-slate-400 lg:text-gray-500">
                    {selectedDDT.fornitore} • Ordine #{selectedDDT.numero}
                  </p>
                </div>
                <button onClick={resetDDTModal} className="p-2 hover:bg-slate-800 lg:hover:bg-gray-100 rounded-xl">
                  <X className="h-5 w-5 text-slate-400 lg:text-gray-500" />
                </button>
              </div>
              
              {/* Prodotti ordinati (riepilogo) */}
              {selectedDDT.prodotti_attesi && selectedDDT.prodotti_attesi.length > 0 && (
                <div className="mt-3 p-3 bg-blue-500/10 lg:bg-blue-50 rounded-xl">
                  <p className="text-xs font-medium text-blue-400 lg:text-blue-700 mb-1">
                    📦 {selectedDDT.prodotti_attesi.length} prodotti ordinati
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {selectedDDT.prodotti_attesi.slice(0, 5).map((p, idx) => (
                      <span key={idx} className="text-xs px-2 py-0.5 bg-blue-500/20 lg:bg-blue-100 text-blue-300 lg:text-blue-700 rounded-full">
                        {p.nome} ({p.quantita} {p.unita_misura})
                      </span>
                    ))}
                    {selectedDDT.prodotti_attesi.length > 5 && (
                      <span className="text-xs text-blue-400 lg:text-blue-600">+{selectedDDT.prodotti_attesi.length - 5} altri</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-5 space-y-4">
              {/* STEP 1: Upload File */}
              {ddtStep === 1 && (
                <>
                  <p className="text-sm text-slate-400 lg:text-gray-600">
                    Scatta una foto o carica il DDT ricevuto. L&apos;AI lo confronterà con l&apos;ordine.
                  </p>
                  
                  {!selectedFile ? (
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-blue-500/30 lg:border-blue-200 rounded-xl hover:bg-blue-500/10 lg:hover:bg-blue-50 cursor-pointer transition-colors">
                        <Camera className="h-8 w-8 text-blue-400 mb-2" />
                        <span className="text-sm font-medium text-blue-400 lg:text-blue-600">Scatta Foto</span>
                        <input 
                          type="file" 
                          accept="image/*" 
                          capture="environment"
                          className="hidden"
                          onChange={handleDDTFileSelect}
                        />
                      </label>
                      
                      <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-600 lg:border-gray-200 rounded-xl hover:bg-slate-800 lg:hover:bg-gray-50 cursor-pointer transition-colors">
                        <Upload className="h-8 w-8 text-slate-400 mb-2" />
                        <span className="text-sm font-medium text-slate-400 lg:text-gray-600">Carica File</span>
                        <input 
                          type="file" 
                          accept="image/*,.pdf"
                          className="hidden"
                          onChange={handleDDTFileSelect}
                        />
                      </label>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Preview file selezionato */}
                      <div className="relative bg-slate-800 lg:bg-gray-100 rounded-xl p-4">
                        {previewUrl && (
                          <img src={previewUrl} alt="Preview DDT" className="w-full max-h-64 object-contain rounded-lg" />
                        )}
                        {!previewUrl && selectedFile.tipoFile === 'pdf' && (
                          <div className="flex items-center justify-center h-40">
                            <FileText className="h-16 w-16 text-red-400" />
                          </div>
                        )}
                        <p className="text-center text-sm text-slate-400 lg:text-gray-500 mt-2">{selectedFile.name}</p>
                        <button 
                          onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                          className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full"
                        >
                          <X className="h-4 w-4 text-white" />
                        </button>
                      </div>
                      
                      {/* Pulsante Analizza */}
                      <Button 
                        onClick={analyzeDDTFile}
                        disabled={analyzingDDT}
                        className="w-full bg-blue-600 hover:bg-blue-700 rounded-xl h-12"
                      >
                        {analyzingDDT ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Analisi in corso...
                          </>
                        ) : (
                          <>
                            <Zap className="h-4 w-4 mr-2" />
                            Analizza DDT
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
              
              {/* STEP 2: Verifica Prodotti + Confronto Ordine */}
              {ddtStep === 2 && (
                <>
                  {/* CONFRONTO ORDINE vs DDT */}
                  {confrontoOrdine && confrontoOrdine.items && confrontoOrdine.items.length > 0 && (
                    <div className="p-4 bg-gradient-to-r from-purple-500/20 to-blue-500/20 lg:from-purple-50 lg:to-blue-50 rounded-xl border border-purple-500/30 lg:border-purple-200">
                      <div className="flex items-center gap-2 mb-3">
                        <Package className="h-5 w-5 text-purple-400 lg:text-purple-600" />
                        <h3 className="font-bold text-white lg:text-gray-900">Confronto Ordine vs DDT</h3>
                      </div>
                      
                      {/* Statistiche */}
                      <div className="grid grid-cols-4 gap-2 mb-3">
                        <div className="text-center p-2 bg-green-500/20 rounded-lg">
                          <p className="text-lg font-bold text-green-400 lg:text-green-600">
                            {confrontoOrdine.items.filter(i => i.status === 'ok').length}
                          </p>
                          <p className="text-xs text-green-300 lg:text-green-700">OK</p>
                        </div>
                        <div className="text-center p-2 bg-yellow-500/20 rounded-lg">
                          <p className="text-lg font-bold text-yellow-400 lg:text-yellow-600">
                            {confrontoOrdine.items.filter(i => i.status === 'variazione').length}
                          </p>
                          <p className="text-xs text-yellow-300 lg:text-yellow-700">Variazioni</p>
                        </div>
                        <div className="text-center p-2 bg-red-500/20 rounded-lg">
                          <p className="text-lg font-bold text-red-400 lg:text-red-600">
                            {confrontoOrdine.items.filter(i => i.status === 'mancante').length}
                          </p>
                          <p className="text-xs text-red-300 lg:text-red-700">Mancanti</p>
                        </div>
                        <div className="text-center p-2 bg-blue-500/20 rounded-lg">
                          <p className="text-lg font-bold text-blue-400 lg:text-blue-600">
                            {confrontoOrdine.items.filter(i => i.status === 'extra').length}
                          </p>
                          <p className="text-xs text-blue-300 lg:text-blue-700">Extra</p>
                        </div>
                      </div>

                      {/* Totali */}
                      <div className="flex justify-between text-sm mb-3 px-2">
                        <div>
                          <span className="text-slate-400 lg:text-gray-500">Ordinato: </span>
                          <span className="font-bold text-white lg:text-gray-900">€{confrontoOrdine.totaleOrdinato?.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 lg:text-gray-500">Ricevuto: </span>
                          <span className={`font-bold ${confrontoOrdine.totaleRicevuto > confrontoOrdine.totaleOrdinato ? 'text-red-400' : 'text-green-400'}`}>
                            €{confrontoOrdine.totaleRicevuto?.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Dettaglio problemi */}
                      {confrontoOrdine.items.some(i => i.status !== 'ok') && (
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {/* Mancanti */}
                          {confrontoOrdine.items.filter(i => i.status === 'mancante').map((item, idx) => (
                            <div key={`m-${idx}`} className="flex items-center gap-2 p-2 bg-red-500/10 rounded text-sm">
                              <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0" />
                              <span className="text-red-300 lg:text-red-700 font-medium truncate">{item.nome}</span>
                              <span className="text-red-200 lg:text-red-500 text-xs ml-auto whitespace-nowrap">
                                NON RICEVUTO
                              </span>
                            </div>
                          ))}
                          
                          {/* Extra */}
                          {confrontoOrdine.items.filter(i => i.status === 'extra').map((item, idx) => (
                            <div key={`e-${idx}`} className="flex items-center gap-2 p-2 bg-blue-500/10 rounded text-sm">
                              <Plus className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              <span className="text-blue-300 lg:text-blue-700 font-medium truncate">{item.nome}</span>
                              <span className="text-blue-200 lg:text-blue-500 text-xs ml-auto whitespace-nowrap">
                                NON ORDINATO
                              </span>
                            </div>
                          ))}
                          
                          {/* Variazioni prezzo */}
                          {confrontoOrdine.items.filter(i => i.status === 'variazione').map((item, idx) => (
                            <div key={`v-${idx}`} className="flex items-center gap-2 p-2 bg-yellow-500/10 rounded text-sm">
                              {item.variazionePrezzo > 0 ? (
                                <TrendingUp className="h-4 w-4 text-red-400 flex-shrink-0" />
                              ) : (
                                <TrendingDown className="h-4 w-4 text-green-400 flex-shrink-0" />
                              )}
                              <span className="text-yellow-300 lg:text-yellow-700 font-medium truncate">{item.nome}</span>
                              <span className={`text-xs ml-auto whitespace-nowrap ${item.variazionePrezzo > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                {item.variazionePrezzo > 0 ? '+' : ''}{item.variazionePrezzo?.toFixed(1)}%
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Tutto OK */}
                      {confrontoOrdine.items.every(i => i.status === 'ok') && (
                        <div className="flex items-center gap-2 p-2 bg-green-500/20 rounded">
                          <CheckCircle className="h-5 w-5 text-green-400" />
                          <span className="text-green-300 lg:text-green-700 font-medium">
                            DDT conforme all&apos;ordine!
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Lista prodotti estratti */}
                  <div className="space-y-2">
                    <h3 className="font-medium text-white lg:text-gray-900">
                      Prodotti estratti ({prodottiVerifica.length})
                    </h3>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {prodottiVerifica.map((p, idx) => (
                        <div key={idx} className="bg-slate-800 lg:bg-gray-50 rounded-xl p-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <input 
                              type="checkbox" 
                              checked={p.arrivato}
                              onChange={(e) => {
                                const updated = [...prodottiVerifica];
                                updated[idx].arrivato = e.target.checked;
                                setProdottiVerifica(updated);
                              }}
                              className="w-5 h-5 rounded border-slate-600"
                            />
                            <div>
                              <p className="font-medium text-white lg:text-gray-900">{p.nome}</p>
                              <div className="flex items-center gap-3 text-xs text-slate-400 lg:text-gray-500">
                                <span>{p.quantita} {p.unita_misura}</span>
                                {p.lotto && <span>Lotto: {p.lotto}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-blue-400 lg:text-blue-600">€{p.prezzo_unitario?.toFixed(2) || '-'}</p>
                            <p className="text-xs text-slate-400 lg:text-gray-500">€{p.importo?.toFixed(2) || '-'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Azioni */}
                  <div className="flex gap-3">
                    <Button 
                      onClick={() => { setDdtStep(1); setSelectedFile(null); setPreviewUrl(null); }}
                      variant="outline"
                      className="flex-1 rounded-xl h-12 border-slate-600 text-slate-300 hover:bg-slate-800"
                    >
                      ← Indietro
                    </Button>
                    <Button 
                      onClick={salvaDDTVerificato}
                      disabled={loading || prodottiVerifica.filter(p => p.arrivato).length === 0}
                      className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl h-12"
                    >
                      {loading ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      Conferma Ricezione
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Risultato Analisi */}
      {showAnalisiModal && analisiResult && (
        <div className="fixed inset-0 bg-black/50 flex items-end lg:items-center justify-center z-50 p-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-t-3xl lg:rounded-2xl w-full lg:max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50">
              <div>
                <h2 className="text-lg font-bold text-green-900">Analisi Completata</h2>
                <p className="text-sm text-green-600">Verifica i dati estratti</p>
              </div>
              <button onClick={() => setShowAnalisiModal(false)} className="p-2 hover:bg-white/50 rounded-xl">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-5 space-y-4">
              {/* Allarmi prezzo */}
              {analisiResult.allarmi_prezzo?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                    <AlertTriangle className="h-4 w-4" />
                    Variazioni prezzo rilevate
                  </div>
                  {analisiResult.allarmi_prezzo.map((p, idx) => (
                    <div key={idx} className="text-sm text-red-600">
                      {p.nome}: €{p.prezzo_unitario} 
                      <span className={p.differenza_prezzo_perc > 0 ? 'text-red-500' : 'text-green-500'}>
                        ({p.differenza_prezzo_perc > 0 ? '+' : ''}{p.differenza_prezzo_perc}%)
                      </span>
                      vs storico €{p.prezzo_storico}
                    </div>
                  ))}
                </div>
              )}

              {/* Prodotti mancanti */}
              {analisiResult.prodotti_mancanti?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                    <XCircle className="h-4 w-4" />
                    Prodotti ordinati ma non ricevuti
                  </div>
                  {analisiResult.prodotti_mancanti.map((p, idx) => (
                    <div key={idx} className="text-sm text-amber-600">
                      {p.nome}: {p.quantita} {p.unita_misura}
                    </div>
                  ))}
                </div>
              )}

              {/* Lista prodotti estratti */}
              <div className="space-y-2">
                <h3 className="font-medium text-gray-900">Prodotti estratti:</h3>
                {analisiResult.dati_estratti?.prodotti?.map((p, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-xl p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{p.nome}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span>{p.quantita} {p.unita_misura}</span>
                        {p.scadenza && <span>Scad: {p.scadenza}</span>}
                        {p.lotto && <span>Lotto: {p.lotto}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-600">€{p.prezzo_unitario || '-'}</p>
                      {p.allarme_prezzo && (
                        <span className="text-xs text-red-500">! Prezzo anomalo</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <Button 
                onClick={() => {
                  toast.success('DDT confermato');
                  setShowAnalisiModal(false);
                  setSelectedDDT(null);
                  loadDDTAttesa();
                }}
                className="w-full bg-green-600 hover:bg-green-700 rounded-xl h-12"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Conferma Ricezione
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Fornitori */}
      {showFornitoriModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end lg:items-center justify-center z-50 p-4">
          <div className="bg-slate-900/95 backdrop-blur-sm lg:bg-white rounded-t-3xl lg:rounded-2xl w-full lg:max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-700 lg:border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white lg:text-gray-900">Gestione Fornitori</h2>
                <p className="text-sm text-slate-400 lg:text-gray-500">{fornitori.length} fornitori dalle fatture</p>
              </div>
              <button onClick={() => { setShowFornitoriModal(false); setSearchFornitore(''); }} className="p-2 hover:bg-slate-800 lg:hover:bg-gray-100 rounded-xl">
                <X className="h-5 w-5 text-slate-400 lg:text-gray-600" />
              </button>
            </div>
            
            {/* Barra di ricerca fornitori */}
            <div className="p-4 border-b border-slate-700 lg:border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Cerca fornitore..."
                  value={searchFornitore}
                  onChange={(e) => setSearchFornitore(e.target.value)}
                  className="w-full pl-10 pr-3 h-10 rounded-xl bg-slate-800/70 lg:bg-gray-100 text-white lg:text-gray-900 placeholder-slate-400 lg:placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 border border-slate-700 lg:border-gray-200"
                />
                {searchFornitore && (
                  <button
                    onClick={() => setSearchFornitore('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-700 lg:hover:bg-gray-200 rounded-full"
                  >
                    <X className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                )}
              </div>
            </div>
            
            <div className="p-5 space-y-2 max-h-96 overflow-y-auto">
              {fornitori
                .filter(f => f.nome?.toLowerCase().includes(searchFornitore.toLowerCase()))
                .map((fornitore) => (
                <div key={fornitore.id} className="bg-slate-800/70 lg:bg-gray-50 rounded-xl p-3 border border-slate-700 lg:border-transparent">
                  {editingFornitore?.nome === fornitore.nome ? (
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-white lg:text-gray-900 mb-2">{fornitore.nome}</p>
                        <Input
                          placeholder="Numero WhatsApp (es. +393331234567)"
                          defaultValue={fornitore.whatsapp}
                          id={`whatsapp-${fornitore.id}`}
                          className="rounded-xl bg-slate-700 lg:bg-white border-slate-600 lg:border-gray-200 text-white lg:text-gray-900 placeholder-slate-400"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => updateFornitore(fornitore, {
                            whatsapp: document.getElementById(`whatsapp-${fornitore.id}`).value
                          })}
                          className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl"
                          size="sm"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Salva
                        </Button>
                        <Button
                          onClick={() => setEditingFornitore(null)}
                          variant="outline"
                          size="sm"
                          className="rounded-xl border-slate-600 lg:border-gray-300 text-slate-300 lg:text-gray-700 hover:bg-slate-700 lg:hover:bg-gray-100"
                        >
                          Annulla
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white lg:text-gray-900 truncate">{fornitore.nome}</p>
                        {fornitore.whatsapp ? (
                          <p className="text-sm text-green-400 lg:text-green-600 flex items-center gap-1 mt-0.5">
                            <span>📱</span> {fornitore.whatsapp}
                          </p>
                        ) : (
                          <p className="text-sm text-amber-400 lg:text-amber-600 mt-0.5">
                            WhatsApp non configurato
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={() => setEditingFornitore(fornitore)}
                        variant="ghost"
                        size="sm"
                        className="rounded-xl text-slate-300 lg:text-gray-600 hover:bg-slate-700 lg:hover:bg-gray-100 ml-2"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Messaggio se nessun risultato */}
              {fornitori.filter(f => f.nome?.toLowerCase().includes(searchFornitore.toLowerCase())).length === 0 && (
                <div className="text-center py-8">
                  <Search className="h-8 w-8 text-slate-500 mx-auto mb-2" />
                  <p className="text-slate-400 lg:text-gray-500 text-sm">Nessun fornitore trovato per "{searchFornitore}"</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Analisi Foto Lista Spesa - Con selezione abbinamenti */}
      {showFotoAnalisiModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end lg:items-center justify-center z-[60]">
          <div className="bg-slate-900/95 backdrop-blur-sm lg:bg-white/90 rounded-t-3xl lg:rounded-2xl w-full lg:max-w-lg max-h-[85vh] lg:max-h-[90vh] flex flex-col mb-16 lg:mb-0">
            {/* Header */}
            <div className="p-4 border-b border-slate-700 lg:border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Camera className="h-5 w-5 text-purple-400 lg:text-purple-600" />
                  <h2 className="text-lg font-bold text-white lg:text-gray-900">Abbina Prodotti</h2>
                </div>
                <button
                  onClick={() => setShowFotoAnalisiModal(false)}
                  disabled={loadingFoto}
                  className="p-2 bg-slate-800 lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 rounded-xl transition-colors"
                >
                  <X className="h-5 w-5 text-white lg:text-gray-600" />
                </button>
              </div>
            </div>

            {/* Content scrollabile */}
            <div data-modal-content="abbina-prodotti" className="p-4 space-y-4 overflow-y-auto flex-1">
              {loadingFoto ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <RefreshCw className="h-10 w-10 text-purple-400 animate-spin mb-4" />
                  <p className="text-slate-300 lg:text-gray-600 text-sm">Analisi in corso...</p>
                  <p className="text-slate-500 lg:text-gray-400 text-xs mt-1">L'AI sta leggendo la tua lista</p>
                </div>
              ) : fotoAnalisiResult?.prodotti?.length > 0 ? (
                <div className="space-y-4">
                  {/* Info */}
                  <div className="bg-blue-900/30 lg:bg-blue-50 rounded-xl p-3 text-center">
                    <p className="text-blue-300 lg:text-blue-700 text-sm">
                      Seleziona il prodotto corretto per ogni voce della lista
                    </p>
                  </div>

                  {/* Lista prodotti da abbinare */}
                  {fotoAnalisiResult.prodotti.map((prodotto, idx) => (
                    <div key={idx} className="bg-slate-800/70 backdrop-blur-sm lg:bg-gray-50 rounded-xl overflow-hidden border border-slate-700 lg:border-gray-200">
                      {/* Header prodotto dalla foto - CON EDITING */}
                      <div className="px-3 py-2 bg-slate-700/50 lg:bg-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-lg font-medium text-gray-500">{prodotto.isNew ? '+' : '•'}</span>
                          
                          {prodotto.editing ? (
                            // Modalità editing
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                type="text"
                                value={prodotto.editValue || ''}
                                onChange={(e) => updateEditValue(idx, e.target.value)}
                                placeholder="Nome prodotto..."
                                className="flex-1 px-2 py-1 rounded bg-slate-600 lg:bg-white text-white lg:text-gray-900 text-sm border border-slate-500 lg:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                                onFocus={(e) => {
                                  // Scroll input into view when keyboard opens
                                  setTimeout(() => {
                                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }, 300);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && prodotto.editValue?.trim()) {
                                    modificaNomeProdotto(idx, prodotto.editValue.trim());
                                  }
                                  if (e.key === 'Escape') {
                                    toggleEditingProdotto(idx);
                                  }
                                }}
                              />
                              <button
                                onClick={() => prodotto.editValue?.trim() && modificaNomeProdotto(idx, prodotto.editValue.trim())}
                                className="p-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                              >
                                <Search className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => toggleEditingProdotto(idx)}
                                className="p-1.5 bg-slate-600 hover:bg-slate-500 lg:bg-gray-300 lg:hover:bg-gray-400 text-white lg:text-gray-700 rounded-lg"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ) : (
                            // Modalità visualizzazione
                            <>
                              <span className="text-sm font-medium text-white lg:text-gray-900 truncate">
                                {prodotto.nome_originale || 'Nuovo prodotto'}
                              </span>
                              <span className="text-xs text-slate-400 lg:text-gray-500">
                                ({prodotto.quantita} {prodotto.unita_misura})
                              </span>
                            </>
                          )}
                        </div>
                        
                        {/* Azioni */}
                        <div className="flex items-center gap-1 ml-2">
                          {prodotto.confermato ? (
                            <span className="text-emerald-400 text-xs flex items-center gap-1">
                              <Check className="h-3 w-3" /> OK
                            </span>
                          ) : !prodotto.editing && (
                            <>
                              <button
                                onClick={() => toggleEditingProdotto(idx)}
                                className="p-1.5 text-slate-400 hover:text-white lg:text-gray-500 lg:hover:text-gray-700 hover:bg-slate-600/50 lg:hover:bg-gray-200 rounded-lg"
                                title="Modifica nome"
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => rimuoviProdottoFoto(idx)}
                                className="p-1.5 text-slate-400 hover:text-red-400 lg:text-gray-500 lg:hover:text-red-500 hover:bg-red-500/20 rounded-lg"
                                title="Rimuovi"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Lista abbinamenti possibili */}
                      {prodotto.abbinamenti?.length > 0 ? (
                        <div className="divide-y divide-slate-700/50 lg:divide-gray-200 max-h-48 overflow-y-auto">
                          {prodotto.abbinamenti.map((abb, abbIdx) => (
                            <div 
                              key={abbIdx}
                              onClick={() => !prodotto.confermato && selezionaAbbinamento(idx, abb)}
                              className={`px-3 py-2.5 cursor-pointer transition-colors ${
                                prodotto.abbinamento_selezionato?.id === abb.id
                                  ? 'bg-blue-600/20 lg:bg-blue-100 border-l-4 border-blue-500'
                                  : 'hover:bg-slate-700/50 lg:hover:bg-gray-100'
                              } ${prodotto.confermato ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    {abb.is_cheapest && (
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-300 lg:bg-emerald-100 lg:text-emerald-700 font-medium">
                                        Più economico
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-white lg:text-gray-900 font-medium truncate mt-1">
                                    {abb.descrizione}
                                  </p>
                                  <p className="text-[11px] text-blue-400 lg:text-blue-600 mt-0.5">
                                    {abb.fornitore} • {abb.num_acquisti} acquisti
                                  </p>
                                </div>
                                <div className="text-right flex-shrink-0 ml-2">
                                  <p className={`text-base font-bold ${abb.is_cheapest ? 'text-emerald-400' : 'text-white lg:text-gray-900'}`}>
                                    €{abb.prezzo?.toFixed(2)}
                                  </p>
                                  <p className="text-[10px] text-slate-400">/{abb.unita_misura}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : !prodotto.editing && (
                        <div className="px-3 py-4 text-center">
                          <p className="text-slate-400 lg:text-gray-500 text-sm">Nessun prodotto trovato</p>
                          <button
                            onClick={() => toggleEditingProdotto(idx)}
                            className="text-blue-400 lg:text-blue-600 text-xs mt-1 hover:underline"
                          >
                            Modifica il nome per cercare di nuovo
                          </button>
                        </div>
                      )}

                      {/* Pulsante conferma singolo prodotto CON MODIFICA QUANTITÀ */}
                      {prodotto.abbinamenti?.length > 0 && prodotto.abbinamento_selezionato && !prodotto.confermato && (
                        <div className="px-3 py-3 bg-slate-700/30 lg:bg-gray-50 border-t border-slate-700/50 lg:border-gray-200 space-y-3">
                          {/* Form modifica quantità */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400 lg:text-gray-500 whitespace-nowrap">Quantità:</span>
                            <input
                              type="number"
                              step="0.1"
                              min="0.1"
                              value={prodotto.quantita_conferma ?? prodotto.quantita ?? 1}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 1;
                                setFotoAnalisiResult(prev => ({
                                  ...prev,
                                  prodotti: prev.prodotti.map((p, i) => 
                                    i === idx ? { ...p, quantita_conferma: val } : p
                                  )
                                }));
                              }}
                              className="w-20 px-2 py-1.5 rounded-lg bg-slate-600 lg:bg-white text-white lg:text-gray-900 text-sm border border-slate-500 lg:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                            />
                            <select
                              value={prodotto.unita_conferma ?? prodotto.unita_misura ?? prodotto.abbinamento_selezionato?.unita_misura ?? 'kg'}
                              onChange={(e) => {
                                setFotoAnalisiResult(prev => ({
                                  ...prev,
                                  prodotti: prev.prodotti.map((p, i) => 
                                    i === idx ? { ...p, unita_conferma: e.target.value } : p
                                  )
                                }));
                              }}
                              className="px-2 py-1.5 rounded-lg bg-slate-600 lg:bg-white text-white lg:text-gray-900 text-sm border border-slate-500 lg:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="kg">Kg</option>
                              <option value="g">g</option>
                              <option value="pz">Pz</option>
                              <option value="ct">Ct</option>
                              <option value="cf">Cf</option>
                              <option value="lt">Lt</option>
                              <option value="cs">Cassa</option>
                            </select>
                            <span className="text-xs text-emerald-400 lg:text-emerald-600 flex-1 text-right">
                              €{((prodotto.abbinamento_selezionato?.prezzo || 0) * (prodotto.quantita_conferma ?? prodotto.quantita ?? 1)).toFixed(2)}
                            </span>
                          </div>
                          
                          <button
                            onClick={() => confermaAbbinamento(idx)}
                            className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                          >
                            <Check className="h-4 w-4" />
                            Conferma e Aggiungi
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Pulsante Aggiungi Prodotto */}
                  <button
                    onClick={aggiungiProdottoManuale}
                    className="w-full py-3 border-2 border-dashed border-slate-600 lg:border-gray-300 rounded-xl text-slate-400 lg:text-gray-500 hover:border-blue-500 hover:text-blue-400 lg:hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Aggiungi altro prodotto
                  </button>

                  {/* Pulsante chiudi */}
                  <button
                    onClick={() => {
                      setShowFotoAnalisiModal(false);
                      setFotoAnalisiResult(null);
                    }}
                    className="w-full py-3 bg-slate-700 lg:bg-gray-200 text-white lg:text-gray-700 rounded-xl text-sm font-medium mt-4"
                  >
                    Chiudi
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <AlertTriangle className="h-10 w-10 text-amber-400 mb-4" />
                  <p className="text-slate-300 lg:text-gray-600 text-sm">Nessun prodotto trovato</p>
                  <p className="text-slate-500 lg:text-gray-400 text-xs mt-1">Prova con un'altra foto</p>
                  {/* Aggiungi manualmente anche quando nessun prodotto trovato */}
                  <button
                    onClick={() => {
                      setFotoAnalisiResult({ prodotti: [] });
                      aggiungiProdottoManuale();
                    }}
                    className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Aggiungi manualmente
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Importa Testo */}
      {showImportTestoModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900/95 backdrop-blur-sm lg:bg-white/90 backdrop-blur-sm rounded-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 bg-slate-800 lg:bg-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-amber-400 lg:text-amber-600" />
                <h3 className="font-semibold text-white lg:text-gray-900">Inserisci Lista</h3>
              </div>
              <button
                onClick={() => {
                  setShowImportTestoModal(false);
                  setTestoImport('');
                }}
                className="p-1.5 hover:bg-slate-700 lg:hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="h-4 w-4 text-slate-400 lg:text-gray-500" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4 space-y-4">
              <p className="text-sm text-slate-400 lg:text-gray-600">
                Scrivi o incolla la lista della spesa (un prodotto per riga):
              </p>
              
              <textarea
                value={testoImport}
                onChange={(e) => setTestoImport(e.target.value)}
                placeholder={`Es:\n- pomodori 2kg\n- mozzarella 5pz\n- pane\n- polpo 1kg\n- olio 2lt`}
                className="w-full h-48 px-3 py-2 rounded-xl bg-slate-800 lg:bg-gray-50 border border-slate-700 lg:border-gray-200 text-white lg:text-gray-900 placeholder-slate-500 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              
              <div className="text-xs text-slate-500 lg:text-gray-400 space-y-1">
                <p><strong>Formati supportati:</strong></p>
                <p>• pomodori 2kg</p>
                <p>• 5 pz mozzarella</p>
                <p>• - pane</p>
                <p>• polpo</p>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowImportTestoModal(false);
                    setTestoImport('');
                  }}
                  className="flex-1 py-2.5 bg-slate-700 lg:bg-gray-200 text-white lg:text-gray-700 rounded-xl text-sm font-medium"
                >
                  Annulla
                </button>
                <button
                  onClick={handleAnalizzaTesto}
                  disabled={!testoImport.trim()}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <Search className="h-4 w-4" />
                  Cerca Prodotti
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close import menu */}
      {showImportMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowImportMenu(false)}
        />
      )}
    </div>
  );
};

export default OrdiniPage;
